/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */
/**
 * ============================================================================
 * FIN SELECTOR V5 - FULL PAGE MODULE (Version H)
 * ============================================================================
 * Split-view layout with wizard on left and results on right
 * Real-time filtering as user changes inputs
 *
 * @version 5.0.0
 * @updated 2025-12-31
 * @author Clément Morandière - Senior Ecommerce Consultant - Waterspot Experts
 *
 * Dependencies:
 * - FinSelectorV5 (fin-selector-v5-core.js)
 * ============================================================================
 */

(function () {
  "use strict";

  const nativeConsole = window.console || {};
  const isDebug = window.FSV5_DEBUG === true;
  const console = {
    log: isDebug && nativeConsole.log ? nativeConsole.log.bind(nativeConsole) : () => {},
    warn: nativeConsole.warn ? nativeConsole.warn.bind(nativeConsole) : () => {},
    error: nativeConsole.error ? nativeConsole.error.bind(nativeConsole) : () => {},
  };

  // ============================================================================
  // CONSTANTS
  // ============================================================================

  const SELECTORS = {
    container: "[data-fsv5-fullpage]",
    productsData: "[data-fsv5-products]",
    productsDataUrl: "[data-fsv5-products-url]",
    disciplinePresets: "[data-fsv5-discipline-presets]",
    sliderConfig: "[data-fsv5-slider-config]",
    disciplineTips: "[data-fsv5-discipline-tips]",
    disciplineTipContainer: "[data-fsv5-discipline-tip]",
    disciplineTipText: "[data-fsv5-discipline-tip-text]",
    resultsGrid: "[data-fsv5-results-grid]",
    resultsCount: "[data-fsv5-count]",
    summary: "[data-fsv5-summary]",
    progressStep: "[data-fsv5-progress-step]",
    section: "[data-fsv5-section]",
    findButton: "[data-fsv5-find]",
    // Input selectors
    skillOption: "[data-fsv5-skill]",
    disciplineOption: "[data-fsv5-discipline]",
    boxOption: "[data-fsv5-box]",
    antiweedToggle: "[data-fsv5-antiweed]",
    slider: "[data-fsv5-slider]",
    sliderFill: "[data-fsv5-slider-fill]",
    sliderValue: "[data-fsv5-slider-value]",
    // Compare
    compareBar: "[data-fsv5-compare-bar]",
    compareCount: "[data-fsv5-compare-count]",
    compareButton: "[data-fsv5-open-compare]",
    compareCheckbox: "[data-fsv5-compare]",
    compareModal: "[data-fsv5-compare-modal]",
    modalClose: "[data-fsv5-modal-close]",
    // Info Modal
    infoButton: "[data-fsv5-info]",
    infoModal: "[data-fsv5-info-modal]",
    infoClose: "[data-fsv5-info-close]",
    // Accordion
    accordionTrigger: "[data-fsv5-accordion-trigger]",
    accordionItem: "[data-fsv5-accordion-item]",
  };

  const DEBOUNCE_DELAY = 150;
  const HIDDEN_PRODUCT_VENDORS = new Set(["select"]);

  // ============================================================================
  // INVENTORY FETCHING CONFIGURATION
  // ============================================================================

  const INVENTORY_CACHE = new Map();
  const INVENTORY_CACHE_TTL = 60000; // 1 minute cache
  const INVENTORY_CACHE_MAX_SIZE = 200; // Maximum cache entries
  const BATCH_FETCH_SIZE = 5; // Products per batch
  const BATCH_FETCH_DELAY = 150; // ms delay between batches
  const INVENTORY_UPDATE_DELAY = 500; // ms delay before fetching after render

  // ============================================================================
  // PRICE FETCHING CONFIGURATION
  // ============================================================================

  const PRICE_CACHE = new Map();
  const PRICE_CACHE_TTL = 300000; // 5 minutes cache (prices change less frequently)
  const PRICE_CACHE_MAX_SIZE = 200; // Maximum cache entries
  const BATCH_FETCH_PRICE_SIZE = 5; // Products per batch
  const BATCH_FETCH_PRICE_DELAY = 150; // ms delay between batches

  // ============================================================================
  // DISCIPLINE DEFAULT VALUES
  // ============================================================================
  // Default slider values per discipline for optimal fin size calculation
  // These values represent typical equipment setups for each discipline
  // Can be overridden via section settings (data-fsv5-discipline-presets JSON)

  const DISCIPLINE_DEFAULTS_FALLBACK = {
    freestyle: { sailSize: 5.0, boardWidth: 60, boardVolume: 100, sailMax: 6, widthMax: 80 },
    wave: { sailSize: 5.0, boardWidth: 60, boardVolume: 90, sailMax: 6, widthMax: 100 },
    freeride: { sailSize: 7.0, boardWidth: 70, boardVolume: 130, sailMax: 9, widthMax: 100 },
    freerace: { sailSize: 7.5, boardWidth: 70, boardVolume: 125, sailMax: 9, widthMax: 100 },
    slalom: { sailSize: 8.0, boardWidth: 65, boardVolume: 115, sailMax: 9, widthMax: 100 },
    speed: { sailSize: 6.5, boardWidth: 55, boardVolume: 75, sailMax: 8, widthMax: 70 },
  };

  // Mutable discipline defaults that can be overridden by section settings
  let DISCIPLINE_DEFAULTS = { ...DISCIPLINE_DEFAULTS_FALLBACK };

  /**
   * Load discipline presets from section settings JSON
   * Merges with fallback defaults to preserve sailMax/widthMax etc.
   */
  function loadDisciplinePresets() {
    const presetsEl = document.querySelector(SELECTORS.disciplinePresets);
    if (!presetsEl) return;

    try {
      const presets = JSON.parse(presetsEl.textContent);
      // Merge presets with fallback defaults
      Object.keys(presets).forEach((discipline) => {
        const preset = presets[discipline];
        const fallback = DISCIPLINE_DEFAULTS_FALLBACK[discipline] || DISCIPLINE_DEFAULTS_FALLBACK.freeride;
        DISCIPLINE_DEFAULTS[discipline] = {
          ...fallback,
          sailSize: preset.sailSize ?? fallback.sailSize,
          boardVolume: preset.boardVolume ?? fallback.boardVolume,
        };
      });
    } catch (e) {
      console.warn("[FSv5] Failed to parse discipline presets:", e);
    }
  }

  /**
   * Check if info buttons should be shown
   * @returns {boolean}
   */
  function shouldShowInfoButton() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return true;
    return container.dataset.fsv5ShowInfo !== "false";
  }

  // Discipline tips loaded from section settings
  let disciplineTips = {};

  /**
   * Load discipline tips from section settings JSON
   */
  function loadDisciplineTips() {
    const tipsEl = document.querySelector(SELECTORS.disciplineTips);
    if (!tipsEl) return;

    try {
      disciplineTips = JSON.parse(tipsEl.textContent);
    } catch (e) {
      console.warn("[FSv5] Failed to parse discipline tips:", e);
    }
  }

  /**
   * Show contextual tip for selected discipline
   * @param {string} discipline - The selected discipline
   */
  function showDisciplineTip(discipline) {
    const tipContainer = document.querySelector(SELECTORS.disciplineTipContainer);
    const tipText = document.querySelector(SELECTORS.disciplineTipText);

    if (!tipContainer || !tipText) return;

    const disciplineLower = discipline ? discipline.toLowerCase().replace(/[\s-_]/g, "") : "";
    const tip = disciplineTips[disciplineLower];

    if (tip) {
      tipText.textContent = tip;
      tipContainer.style.display = "flex";
      // Re-trigger animation
      tipContainer.style.animation = "none";
      tipContainer.offsetHeight; // Force reflow
      tipContainer.style.animation = "fsv5-tip-fade-in 0.3s ease";
    } else {
      tipContainer.style.display = "none";
    }
  }

  /**
   * Hide discipline tip
   */
  function hideDisciplineTip() {
    const tipContainer = document.querySelector(SELECTORS.disciplineTipContainer);
    if (tipContainer) {
      tipContainer.style.display = "none";
    }
  }

  // ============================================================================
  // STATE
  // ============================================================================

  let selector = null;
  let products = [];
  let debounceTimer = null;
  let visibleUpdateTimer = null;
  let liveDataObserver = null;
  let liveDataMutationObserver = null;
  let compareA11yState = null;

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize the fullpage fin selector
   */
  async function initFinSelectorFullpage() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    // Load discipline presets from section settings
    loadDisciplinePresets();

    // Load discipline tips from section settings
    loadDisciplineTips();

    // Load products from JSON data (async)
    await loadProductsData();

    // Initialize core selector
    selector = FinSelectorV5.init(container, {
      products: products,
      onStateChange: handleStateChange,
      onCompareUpdate: handleCompareUpdate,
    });

    // Setup all interactions
    setupSkillSelections(container);
    setupDisciplineSelections(container);
    setupBoxSelections(container);
    setupAntiweedToggle(container);
    setupSliders(container);
    setupFindButton(container);
    setupAccordion(container);
    setupAddToCart(container);

    // Initialize slider constraints based on initial discipline (if set)
    const initialDiscipline = selector?.state?.discipline;
    if (initialDiscipline) {
      updateSliderConstraints(initialDiscipline);
    }

    // Initial state
    updateProgressBar();

    // Setup lazy loading for inventory
    setupLazyInventoryLoading();

    // Initial inventory and price update after render
    scheduleVisibleProductRefresh();
  }

  /**
   * Load products from JSON file URL
   * @returns {Promise<void>}
   */
  async function loadProductsData() {
    // Try URL-based loading first (static JSON file)
    const urlEl = document.querySelector(SELECTORS.productsDataUrl);
    if (urlEl) {
      try {
        const url = JSON.parse(urlEl.textContent);
        const response = await fetch(url);
        if (response.ok) {
          products = await response.json();
          products = hideUnavailableAssortments(products);
          return;
        }
      } catch (e) {
        console.error("[FSV5 Fullpage] Failed to fetch products:", e);
      }
    }

    // Fallback to inline JSON
    const dataEl = document.querySelector(SELECTORS.productsData);
    if (dataEl) {
      try {
        products = JSON.parse(dataEl.textContent);
        products = hideUnavailableAssortments(products);
        return;
      } catch (e) {
        console.error("[FSV5 Fullpage] Failed to parse inline products:", e);
      }
    }

    console.warn("[FSV5 Fullpage] No products data found");
    products = [];
  }

  function hideUnavailableAssortments(productList) {
    if (!Array.isArray(productList)) return [];

    return productList
      .filter((product) => {
        const vendor = String(product?.vendor || "")
          .trim()
          .toLowerCase();
        return !HIDDEN_PRODUCT_VENDORS.has(vendor);
      })
      .map((product) => ({
        ...product,
        price: null,
        price_formatted: "",
        compare_at_price: null,
        compare_at_price_formatted: "",
      }));
  }

  // ============================================================================
  // DYNAMIC INVENTORY FETCHING
  // ============================================================================

  /**
   * Fetch live product inventory from Shopify with retry logic for rate limiting
   * @param {string} handle - Product handle
   * @param {number} retryCount - Current retry attempt (for exponential backoff)
   * @returns {Promise<{available: boolean, inventory_quantity: number}>}
   */
  async function fetchProductInventory(handle, retryCount = 0) {
    if (!handle) {
      console.warn("[FSV5 Inventory] No handle provided");
      return { available: true, inventory_quantity: 0 };
    }

    // Check cache first
    const cached = INVENTORY_CACHE.get(handle);
    if (cached && Date.now() - cached.timestamp < INVENTORY_CACHE_TTL) {
      return cached.data;
    }

    try {
      const response = await fetch(`/products/${handle}.js`);

      // Handle rate limiting (429) with exponential backoff
      if (response.status === 429) {
        if (retryCount < 3) {
          const backoffDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
          console.warn(`[FSV5 Inventory] Rate limited, retrying in ${backoffDelay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, backoffDelay));
          return fetchProductInventory(handle, retryCount + 1);
        } else {
          console.warn("[FSV5 Inventory] Rate limit exceeded after retries:", handle);
          // Clear cache to force fresh fetch later
          INVENTORY_CACHE.delete(handle);
          return { available: true, inventory_quantity: 0 };
        }
      }

      if (!response.ok) {
        if (response.status === 404) {
          console.warn("[FSV5 Inventory] Product not found:", handle);
          // Cache the 404 result to avoid repeated failed requests
          const notFoundData = { available: false, inventory_quantity: 0 };
          INVENTORY_CACHE.set(handle, {
            data: notFoundData,
            timestamp: Date.now(),
          });
          return notFoundData;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const product = await response.json();

      // Handle edge cases: no variants, missing data
      if (!product.variants || product.variants.length === 0) {
        console.warn("[FSV5 Inventory] Product has no variants:", handle);
        const noVariantData = {
          available: product.available || false,
          inventory_quantity: 0,
        };
        INVENTORY_CACHE.set(handle, {
          data: noVariantData,
          timestamp: Date.now(),
        });
        return noVariantData;
      }

      // Get inventory from first available variant or first variant
      const variant = product.variants.find((v) => v.available) || product.variants[0];
      const inventoryData = {
        available: product.available || false,
        inventory_quantity: variant?.inventory_quantity ?? 0,
      };

      // Cache the result (with LRU eviction if cache is too large)
      if (INVENTORY_CACHE.size >= INVENTORY_CACHE_MAX_SIZE) {
        // Remove oldest entry (simple LRU - remove first entry)
        const firstKey = INVENTORY_CACHE.keys().next().value;
        if (firstKey) {
          INVENTORY_CACHE.delete(firstKey);
        }
      }

      INVENTORY_CACHE.set(handle, {
        data: inventoryData,
        timestamp: Date.now(),
      });

      return inventoryData;
    } catch (error) {
      console.warn("[FSV5 Inventory] Failed to fetch inventory for", handle, error);

      // Clear cache on error to force fresh fetch next time
      INVENTORY_CACHE.delete(handle);

      // Return fallback - don't block UI
      return { available: true, inventory_quantity: 0 };
    }
  }

  /**
   * Batch fetch inventory for multiple products
   * @param {Array<string>} handles - Product handles
   * @returns {Promise<Map<string, Object>>} Map of handle to inventory data
   */
  async function batchFetchInventory(handles) {
    const inventoryMap = new Map();

    if (!handles || handles.length === 0) {
      return inventoryMap;
    }

    // Filter out invalid handles
    const validHandles = handles.filter((h) => h && typeof h === "string" && h.trim().length > 0);

    if (validHandles.length === 0) {
      return inventoryMap;
    }

    // Process in batches to avoid rate limiting
    for (let i = 0; i < validHandles.length; i += BATCH_FETCH_SIZE) {
      const batch = validHandles.slice(i, i + BATCH_FETCH_SIZE);
      const batchNum = Math.floor(i / BATCH_FETCH_SIZE) + 1;
      const totalBatches = Math.ceil(validHandles.length / BATCH_FETCH_SIZE);

      const promises = batch.map(async (handle) => {
        try {
          const data = await fetchProductInventory(handle);
          if (data) {
            inventoryMap.set(handle, data);
          }
        } catch (error) {
          console.warn("[FSV5 Inventory] Error fetching inventory for", handle, error);
          // Continue with other products even if one fails
          // Set fallback data
          inventoryMap.set(handle, { available: true, inventory_quantity: 0 });
        }
      });

      await Promise.all(promises);

      // Small delay between batches to respect rate limits
      if (i + BATCH_FETCH_SIZE < validHandles.length) {
        await new Promise((resolve) => setTimeout(resolve, BATCH_FETCH_DELAY));
      }
    }

    return inventoryMap;
  }

  /**
   * Enrich products with live inventory data
   * @param {Array} products - Product array
   * @returns {Promise<Array>} Products with updated inventory
   */
  async function enrichProductsWithInventory(products) {
    if (!products || products.length === 0) {
      return products;
    }

    try {
      // Get unique handles
      const handles = [...new Set(products.map((p) => p && p.handle).filter(Boolean))];

      if (handles.length === 0) {
        return products;
      }

      // Fetch inventory for all products
      const inventoryMap = await batchFetchInventory(handles);

      // Update products with live inventory
      let updatedCount = 0;
      const enriched = products.map((product) => {
        if (!product || !product.handle) return product;

        const inventory = inventoryMap.get(product.handle);
        if (inventory) {
          updatedCount++;
          return {
            ...product,
            available: inventory.available,
            inventory_quantity: inventory.inventory_quantity,
            _inventoryFetched: true, // Mark that we have live inventory data
          };
        }
        return product;
      });

      return enriched;
    } catch (error) {
      console.error("[FSV5 Inventory] Error enriching products:", error);
      // Return original products if enrichment fails
      return products;
    }
  }

  // ============================================================================
  // DYNAMIC PRICE FETCHING
  // ============================================================================

  /**
   * Read the active storefront currency instead of trusting product JSON,
   * which can be returned in the shop base currency on localized markets.
   * @param {string} fallback - Fallback currency code
   * @returns {string} Active currency code
   */
  function getActiveCurrency(fallback = "EUR") {
    return window.Shopify?.currency?.active || window.themeVariables?.settings?.currencyCode || fallback || "EUR";
  }

  /**
   * Format price from cents to currency string
   * @param {number} cents - Price in cents
   * @param {string} currency - Currency code (default: EUR)
   * @returns {string} Formatted price string
   */
  function formatPrice(cents, currency = "EUR") {
    currency = getActiveCurrency(currency);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency,
      }).format(cents / 100);
    } catch (error) {
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  }

  /**
   * Fetch live product price from Shopify with retry logic for rate limiting
   * @param {string} handle - Product handle
   * @param {number} retryCount - Current retry attempt (for exponential backoff)
   * @returns {Promise<{price: number, price_formatted: string, compare_at_price?: number, compare_at_price_formatted?: string}>}
   */
  async function fetchProductPrice(handle, retryCount = 0) {
    if (!handle) {
      console.warn("[FSV5 Price] No handle provided");
      return null;
    }

    // Check cache first
    const cached = PRICE_CACHE.get(handle);
    if (cached && Date.now() - cached.timestamp < PRICE_CACHE_TTL) {
      return cached.data;
    }

    try {
      const response = await fetch(`/products/${handle}.js`);

      // Handle rate limiting (429) with exponential backoff
      if (response.status === 429) {
        if (retryCount < 3) {
          const backoffDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
          console.warn(`[FSV5 Price] Rate limited, retrying in ${backoffDelay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, backoffDelay));
          return fetchProductPrice(handle, retryCount + 1);
        } else {
          console.warn("[FSV5 Price] Rate limit exceeded after retries:", handle);
          PRICE_CACHE.delete(handle);
          return null;
        }
      }

      if (!response.ok) {
        if (response.status === 404) {
          console.warn("[FSV5 Price] Product not found:", handle);
          return null;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const product = await response.json();

      if (!product.variants || product.variants.length === 0) {
        console.warn("[FSV5 Price] Product has no variants:", handle);
        return null;
      }

      // Get price from first available variant or first variant
      const variant = product.variants.find((v) => v.available) || product.variants[0];
      const priceInCents = variant.price; // Shopify returns price in cents
      const compareAtPrice = variant.compare_at_price ? variant.compare_at_price : null;
      const currency = getActiveCurrency(product.currency || "EUR");

      const priceData = {
        price: priceInCents,
        price_formatted: formatPrice(priceInCents, currency),
        compare_at_price: compareAtPrice,
        compare_at_price_formatted: compareAtPrice ? formatPrice(compareAtPrice, currency) : null,
      };

      // Cache the result (with LRU eviction if cache is too large)
      if (PRICE_CACHE.size >= PRICE_CACHE_MAX_SIZE) {
        // Remove oldest entry (simple LRU - remove first entry)
        const firstKey = PRICE_CACHE.keys().next().value;
        if (firstKey) {
          PRICE_CACHE.delete(firstKey);
        }
      }

      PRICE_CACHE.set(handle, {
        data: priceData,
        timestamp: Date.now(),
      });

      return priceData;
    } catch (error) {
      console.warn("[FSV5 Price] Failed to fetch price for", handle, error);

      // Clear cache on error to force fresh fetch next time
      PRICE_CACHE.delete(handle);

      // Return null and leave the price hidden rather than showing a stale value.
      return null;
    }
  }

  /**
   * Batch fetch prices for multiple products
   * @param {Array<string>} handles - Product handles
   * @returns {Promise<Map<string, Object>>} Map of handle to price data
   */
  async function batchFetchPrices(handles) {
    const priceMap = new Map();

    if (!handles || handles.length === 0) {
      return priceMap;
    }

    // Filter out invalid handles
    const validHandles = handles.filter((h) => h && typeof h === "string" && h.trim().length > 0);

    if (validHandles.length === 0) {
      return priceMap;
    }

    // Process in batches to avoid rate limiting
    for (let i = 0; i < validHandles.length; i += BATCH_FETCH_PRICE_SIZE) {
      const batch = validHandles.slice(i, i + BATCH_FETCH_PRICE_SIZE);
      const batchNum = Math.floor(i / BATCH_FETCH_PRICE_SIZE) + 1;
      const totalBatches = Math.ceil(validHandles.length / BATCH_FETCH_PRICE_SIZE);

      const promises = batch.map(async (handle) => {
        try {
          const data = await fetchProductPrice(handle);
          if (data) {
            priceMap.set(handle, data);
          }
        } catch (error) {
          console.warn("[FSV5 Price] Error fetching price for", handle, error);
        }
      });

      await Promise.all(promises);

      // Delay between batches to respect rate limits
      if (i + BATCH_FETCH_PRICE_SIZE < validHandles.length) {
        await new Promise((resolve) => setTimeout(resolve, BATCH_FETCH_PRICE_DELAY));
      }
    }

    return priceMap;
  }

  /**
   * Enrich products array with live prices
   * @param {Array} products - Products to enrich
   * @returns {Promise<Array>} Products with updated prices
   */
  async function enrichProductsWithPrices(products) {
    if (!products || products.length === 0) {
      return products;
    }

    // Get all unique handles
    const handles = [...new Set(products.map((p) => p.handle).filter(Boolean))];

    // Fetch prices in batches
    const priceMap = await batchFetchPrices(handles);

    // Update products with live prices
    const enriched = products.map((product) => {
      const priceData = priceMap.get(product.handle);
      if (priceData) {
        return {
          ...product,
          price: priceData.price,
          price_formatted: priceData.price_formatted,
          compare_at_price: priceData.compare_at_price,
          compare_at_price_formatted: priceData.compare_at_price_formatted,
        };
      }
      return {
        ...product,
        price: null,
        price_formatted: "",
        compare_at_price: null,
        compare_at_price_formatted: "",
      };
    });

    return enriched;
  }

  /**
   * Update product card price display
   * @param {HTMLElement} cardEl - Product card element
   * @param {Object} product - Product data with price
   */
  function updateProductCardPrice(cardEl, product) {
    if (!cardEl || !product || !product.price_formatted) {
      return;
    }

    // Find price container (matches renderProductCard structure)
    const priceContainer = cardEl.querySelector(".fsv5-product-card__price");
    if (!priceContainer) {
      // Fallback: try data attribute selector
      const priceEl = cardEl.querySelector("[data-fsv5-product-price]");
      if (priceEl) {
        priceEl.textContent = product.price_formatted;
        priceEl.classList.add("fsv5-price-updated");
      }
      return;
    }

    priceContainer.textContent = product.price_formatted;

    priceContainer.classList.add("fsv5-price-updated");
  }

  /**
   * Update prices for all currently visible product cards
   */
  async function updateVisibleProductsPrices() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) {
      console.warn("[FSV5 Price] Container not found for price update");
      return;
    }

    const productCards = container.querySelectorAll("[data-fsv5-product-handle]");
    if (productCards.length === 0) {
      return;
    }

    // Get handles of visible products
    const handles = Array.from(productCards)
      .map((card) => card.dataset.fsv5ProductHandle)
      .filter(Boolean);

    if (handles.length === 0) {
      return;
    }

    // Fetch prices
    const priceMap = await batchFetchPrices(handles);

    // Update cards
    let updatedCount = 0;
    productCards.forEach((card) => {
      const handle = card.dataset.fsv5ProductHandle;
      const priceData = priceMap.get(handle);

      if (priceData) {
        const product = products.find((p) => p.handle === handle);
        if (product) {
          // Update product data
          product.price = priceData.price;
          product.price_formatted = priceData.price_formatted;
          product.compare_at_price = priceData.compare_at_price;
          product.compare_at_price_formatted = priceData.compare_at_price_formatted;

          // Update card UI
          updateProductCardPrice(card, product);
          updatedCount++;
        }
      }
    });
  }

  /**
   * Update stock status for a product card element
   * Called when live inventory data is fetched
   * @param {HTMLElement} cardEl - Product card element
   * @param {Object} product - Product data with inventory
   */
  function updateProductCardStock(cardEl, product) {
    if (!cardEl || !product) {
      console.warn("[FSV5 Inventory] Cannot update card stock: missing card or product");
      return;
    }

    // Calculate stock status from live inventory data
    let stockStatus = "in-stock";
    let stockText = "In Stock";

    if (!product.available) {
      stockStatus = "out-of-stock";
      stockText = "Out of Stock";
    } else if (
      product.inventory_quantity !== undefined &&
      product.inventory_quantity <= 5 &&
      product.inventory_quantity > 0
    ) {
      stockStatus = "low-stock";
      stockText = `${product.inventory_quantity} left`;
    }

    // Update stock dot - remove loading state and all other status classes
    const stockDot = cardEl.querySelector(".fsv5-product-card__stock-dot");
    if (stockDot) {
      stockDot.classList.remove(
        "fsv5-product-card__stock-dot--loading",
        "fsv5-product-card__stock-dot--in-stock",
        "fsv5-product-card__stock-dot--low-stock",
        "fsv5-product-card__stock-dot--out-of-stock",
      );
      stockDot.classList.add(`fsv5-product-card__stock-dot--${stockStatus}`);
    }

    // Update stock text - remove loading state and all other status classes
    const stockTextEl = cardEl.querySelector(".fsv5-product-card__stock-text");
    if (stockTextEl) {
      stockTextEl.classList.remove(
        "fsv5-product-card__stock-text--loading",
        "fsv5-product-card__stock-text--in-stock",
        "fsv5-product-card__stock-text--low-stock",
        "fsv5-product-card__stock-text--out-of-stock",
      );
      stockTextEl.classList.add(`fsv5-product-card__stock-text--${stockStatus}`);
      stockTextEl.textContent = stockText;
    }

    // Update button state based on live availability
    const button = cardEl.querySelector(".fsv5-product-card__btn");
    if (button) {
      if (product.available) {
        button.classList.remove("fsv5-product-card__btn--disabled");
        // If it was a span (disabled state), replace with button
        if (button.tagName === "SPAN") {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "fsv5-product-card__btn";
          btn.setAttribute("data-fsv5-add-to-cart", "");
          btn.textContent = "Add to Cart";
          button.replaceWith(btn);
        }
      } else {
        button.classList.add("fsv5-product-card__btn--disabled");
        // If it was a button, replace with disabled span
        if (button.tagName === "BUTTON" || button.tagName === "A") {
          const span = document.createElement("span");
          span.className = "fsv5-product-card__btn fsv5-product-card__btn--disabled";
          span.textContent = "Out of Stock";
          button.replaceWith(span);
        }
      }
    }
  }

  /**
   * Update all visible product cards with live inventory
   */
  async function updateVisibleProductsInventory() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) {
      console.warn("[FSV5 Inventory] Container not found for inventory update");
      return;
    }

    const productCards = container.querySelectorAll("[data-fsv5-product-handle]");
    if (productCards.length === 0) {
      return;
    }

    // Get handles from visible cards
    const handles = Array.from(productCards)
      .map((card) => card.dataset.fsv5ProductHandle)
      .filter(Boolean);

    if (handles.length === 0) {
      return;
    }

    // Fetch inventory for visible products
    const inventoryMap = await batchFetchInventory(handles);

    // Update each card
    let updatedCount = 0;
    productCards.forEach((card) => {
      const handle = card.dataset.fsv5ProductHandle;
      const inventory = inventoryMap.get(handle);

      if (inventory) {
        // Find product in current products array
        const product = products.find((p) => p.handle === handle);
        if (product) {
          // Update product data with live inventory
          product.available = inventory.available;
          product.inventory_quantity = inventory.inventory_quantity;
          product._inventoryFetched = true; // Mark as having live data

          // Update card UI
          updateProductCardStock(card, product);
          updatedCount++;
        }
      }
    });
  }

  /**
   * Setup lazy loading for inventory using Intersection Observer
   */
  function setupLazyInventoryLoading() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    if (!("IntersectionObserver" in window)) {
      updateVisibleProductsInventory();
      updateVisibleProductsPrices();
      return;
    }

    if (liveDataObserver) {
      observeLiveProductCards(container);
      return;
    }

    liveDataObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const card = entry.target;
            const handle = card.dataset.fsv5ProductHandle;

            if (handle && card.dataset.fsv5LiveLoaded !== "true") {
              card.dataset.fsv5LiveLoaded = "true";

              // Fetch inventory for this product
              fetchProductInventory(handle).then((inventory) => {
                const product = products.find((p) => p.handle === handle);
                if (product) {
                  // Update product data with live inventory
                  product.available = inventory.available;
                  product.inventory_quantity = inventory.inventory_quantity;
                  product._inventoryFetched = true; // Mark as having live data

                  // Update card UI
                  updateProductCardStock(card, product);
                }
              });

              // Fetch price for this product
              fetchProductPrice(handle).then((priceData) => {
                const product = products.find((p) => p.handle === handle);
                if (product && priceData) {
                  // Update product data
                  product.price = priceData.price;
                  product.price_formatted = priceData.price_formatted;
                  product.compare_at_price = priceData.compare_at_price;
                  product.compare_at_price_formatted = priceData.compare_at_price_formatted;

                  // Update card UI
                  updateProductCardPrice(card, product);
                }
              });

              // Unobserve after fetching to avoid duplicate requests
              liveDataObserver.unobserve(card);
            }
          }
        });
      },
      { rootMargin: "50px" },
    ); // Start loading 50px before visible

    observeLiveProductCards(container);

    // Use MutationObserver to watch for new cards
    liveDataMutationObserver = new MutationObserver(() => observeLiveProductCards(container));
    liveDataMutationObserver.observe(container.querySelector(SELECTORS.resultsGrid) || container, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Observe newly rendered product cards for live price and inventory updates.
   */
  function observeLiveProductCards(container = document.querySelector(SELECTORS.container)) {
    if (!container || !liveDataObserver) return;

    const cards = container.querySelectorAll("[data-fsv5-product-handle]");
    let observedCount = 0;
    cards.forEach((card) => {
      if (card.dataset.fsv5InventoryObserved !== "true" && card.dataset.fsv5LiveLoaded !== "true") {
        card.dataset.fsv5InventoryObserved = "true";
        liveDataObserver.observe(card);
        observedCount++;
      }
    });

    if (observedCount > 0) {
    }
  }

  // ============================================================================
  // INPUT HANDLERS
  // ============================================================================

  /**
   * Setup skill level selections
   */
  function setupSkillSelections(container) {
    const options = container.querySelectorAll(SELECTORS.skillOption);

    options.forEach((option) => {
      option.addEventListener("click", () => {
        const skill = option.dataset.fsv5Skill;

        // Update visual state
        options.forEach((o) => {
          o.classList.remove("fsv5-skill-option--selected", "fsv5-skill-card--selected");
          o.setAttribute("aria-pressed", "false");
        });
        option.classList.add(
          option.classList.contains("fsv5-skill-card") ? "fsv5-skill-card--selected" : "fsv5-skill-option--selected",
        );
        option.setAttribute("aria-pressed", "true");

        // Update selector state
        selector.updateState("skillLevel", skill);
        markSectionComplete("skill");
        updateResultsRealtime();
      });
    });
  }

  /**
   * Update slider constraints and default values based on discipline
   * Sets both max constraints AND default values for optimal fin size calculation
   * This ensures that when a discipline is selected, sliders show typical values
   * which generates a coherent recommended fin size range
   */
  function updateSliderConstraints(discipline) {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    const sailSlider = container.querySelector('[data-fsv5-slider="sail_size"]');
    const boardWidthSlider = container.querySelector('[data-fsv5-slider="board_width"]');
    const boardVolumeSlider = container.querySelector('[data-fsv5-slider="board_volume"]');

    if (!sailSlider || !boardWidthSlider) return;

    const disciplineLower = discipline ? discipline.toLowerCase().replace(/[\s-_]/g, "") : "";

    // Get defaults for this discipline, or use freeride as fallback
    const defaults = DISCIPLINE_DEFAULTS[disciplineLower] || DISCIPLINE_DEFAULTS.freeride;
    const fallbackDefaults = { sailMax: 12.5, widthMax: 100 };

    // Apply max constraints
    const sailMax = defaults.sailMax || fallbackDefaults.sailMax;
    const widthMax = defaults.widthMax || fallbackDefaults.widthMax;

    sailSlider.max = String(sailMax);
    sailSlider.setAttribute("max", String(sailMax));
    sailSlider.setAttribute("aria-valuemax", String(sailMax));

    boardWidthSlider.max = String(widthMax);
    boardWidthSlider.setAttribute("max", String(widthMax));
    boardWidthSlider.setAttribute("aria-valuemax", String(widthMax));

    // Update max label display
    const sailMarks = sailSlider.parentElement?.querySelector(".fsv5-slider__marks");
    if (sailMarks) {
      const marks = sailMarks.querySelectorAll("span");
      if (marks.length > 1) marks[1].textContent = `${sailMax}m²`;
    }

    const widthMarks = boardWidthSlider.parentElement?.querySelector(".fsv5-slider__marks");
    if (widthMarks) {
      const marks = widthMarks.querySelectorAll("span");
      if (marks.length > 1) marks[1].textContent = `${widthMax}cm`;
    }

    // Set default VALUES for the discipline (not just constraints)
    // This is the key fix: when user selects a discipline, sliders update to typical values
    if (defaults.sailSize) {
      const newSailValue = Math.min(defaults.sailSize, sailMax);
      sailSlider.value = String(newSailValue);
      updateSliderState("sail_size", newSailValue);
      updateSliderVisuals(sailSlider, "sail_size");
    }

    if (defaults.boardWidth) {
      const newWidthValue = Math.min(defaults.boardWidth, widthMax);
      boardWidthSlider.value = String(newWidthValue);
      updateSliderState("board_width", newWidthValue);
      updateSliderVisuals(boardWidthSlider, "board_width");
    }

    if (defaults.boardVolume && boardVolumeSlider) {
      boardVolumeSlider.value = String(defaults.boardVolume);
      updateSliderState("board_volume", defaults.boardVolume);
      updateSliderVisuals(boardVolumeSlider, "board_volume");
    }

    // Recalculate recommended size with new slider values
    if (selector) {
      const newSize = selector.calculateRecommendedSize();
      selector.updateState("recommendedSize", newSize);
      selector.updateState("recommendedRange", selector.calculateRange(newSize));
    }
  }

  /**
   * Setup discipline selections
   */
  function setupDisciplineSelections(container) {
    const options = container.querySelectorAll(SELECTORS.disciplineOption);

    options.forEach((option) => {
      option.addEventListener("click", () => {
        const discipline = option.dataset.fsv5Discipline;

        // Update visual state
        options.forEach((o) => {
          o.classList.remove("fsv5-discipline-option--selected", "fsv5-discipline-card--selected");
          o.setAttribute("aria-pressed", "false");
        });
        option.classList.add(
          option.classList.contains("fsv5-discipline-card")
            ? "fsv5-discipline-card--selected"
            : "fsv5-discipline-option--selected",
        );
        option.setAttribute("aria-pressed", "true");

        // Update selector state
        selector.updateState("discipline", discipline);
        markSectionComplete("discipline");

        // Update slider constraints based on discipline
        updateSliderConstraints(discipline);

        // Update box compatibility
        updateBoxCompatibility(discipline);

        // Show contextual tip for this discipline
        showDisciplineTip(discipline);

        updateResultsRealtime();
      });
    });
  }

  /**
   * Setup box type selections
   */
  function setupBoxSelections(container) {
    const options = container.querySelectorAll(SELECTORS.boxOption);

    options.forEach((option) => {
      option.addEventListener("click", () => {
        // Skip if disabled
        if (
          option.classList.contains("fsv5-box-option--disabled") ||
          option.classList.contains("fsv5-box-card--disabled")
        ) {
          return;
        }

        const boxType = option.dataset.fsv5Box;

        // Update visual state
        options.forEach((o) => {
          o.classList.remove("fsv5-box-option--selected", "fsv5-box-card--selected");
          o.setAttribute("aria-pressed", "false");
        });
        option.classList.add(
          option.classList.contains("fsv5-box-card") ? "fsv5-box-card--selected" : "fsv5-box-option--selected",
        );
        option.setAttribute("aria-pressed", "true");

        // Update selector state
        selector.updateState("boxType", boxType);
        markSectionComplete("box");
        updateResultsRealtime();
      });
    });
  }

  /**
   * Update box compatibility based on discipline
   */
  function updateBoxCompatibility(discipline) {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    const compatibility = FinSelectorV5.getBoxCompatibility(discipline);
    const boxOptions = container.querySelectorAll(SELECTORS.boxOption);

    boxOptions.forEach((option) => {
      const boxType = option.dataset.fsv5Box;
      const isCompatible = compatibility[boxType] !== false;

      // Update classes
      option.classList.toggle("fsv5-box-option--disabled", !isCompatible);
      option.classList.toggle("fsv5-box-card--disabled", !isCompatible);
      option.setAttribute("aria-disabled", !isCompatible);
      option.dataset.fsv5BoxCompatible = isCompatible;

      // Clear selection if incompatible
      if (!isCompatible && option.classList.contains("fsv5-box-option--selected")) {
        option.classList.remove("fsv5-box-option--selected", "fsv5-box-card--selected");
        option.setAttribute("aria-pressed", "false");
        selector.updateState("boxType", null);
      }
    });
  }

  /**
   * Setup antiweed toggle
   */
  function setupAntiweedToggle(container) {
    const toggles = container.querySelectorAll(SELECTORS.antiweedToggle);

    toggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        const isActive = toggle.classList.toggle("fsv5-toggle--active");
        toggle.setAttribute("aria-pressed", isActive);

        selector.updateState("antiweed", isActive);
        updateResultsRealtime();
      });
    });
  }

  /**
   * Setup sliders with debounced updates
   */
  function setupSliders(container) {
    const sliders = container.querySelectorAll(SELECTORS.slider);

    sliders.forEach((slider) => {
      const sliderType = slider.dataset.fsv5Slider;

      // Input event for real-time visual feedback
      slider.addEventListener("input", () => {
        updateSliderVisuals(slider, sliderType);
        updateQuickSelectButtons(sliderType, parseFloat(slider.value));
      });

      // Change event for state update (debounced)
      slider.addEventListener("change", () => {
        const value = parseFloat(slider.value);
        updateSliderState(sliderType, value);
        updateQuickSelectButtons(sliderType, value);
        markSectionComplete("setup");
        debouncedUpdateResults();
      });

      // Initialize fill
      updateSliderVisuals(slider, sliderType);
    });

    // Setup quick-select buttons
    setupQuickSelectButtons(container);
  }

  /**
   * Setup quick-select buttons for sliders
   */
  function setupQuickSelectButtons(container) {
    const quickSelectGroups = container.querySelectorAll("[data-fsv5-quick-select]");

    quickSelectGroups.forEach((group) => {
      const sliderType = group.dataset.fsv5QuickSelect;
      const buttons = group.querySelectorAll("[data-fsv5-quick-value]");

      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          const value = parseFloat(button.dataset.fsv5QuickValue);
          const slider = container.querySelector(`[data-fsv5-slider="${sliderType}"]`);

          if (slider) {
            slider.value = value;
            updateSliderVisuals(slider, sliderType);
            updateQuickSelectButtons(sliderType, value);
            updateSliderState(sliderType, value);
            markSectionComplete("setup");
            debouncedUpdateResults();
          }
        });
      });
    });
  }

  /**
   * Update quick-select button active states
   */
  function updateQuickSelectButtons(sliderType, value) {
    const container = document.querySelector(SELECTORS.container);
    const quickSelectGroup = container.querySelector(`[data-fsv5-quick-select="${sliderType}"]`);

    if (!quickSelectGroup) return;

    const buttons = quickSelectGroup.querySelectorAll("[data-fsv5-quick-value]");
    buttons.forEach((button) => {
      const buttonValue = parseFloat(button.dataset.fsv5QuickValue);
      if (Math.abs(buttonValue - value) < 0.05) {
        // Small tolerance for floating point
        button.classList.add("fsv5-slider__quick-btn--active");
      } else {
        button.classList.remove("fsv5-slider__quick-btn--active");
      }
    });
  }

  /**
   * Update slider visual elements
   */
  function updateSliderVisuals(slider, sliderType) {
    const container = document.querySelector(SELECTORS.container);
    const value = parseFloat(slider.value);
    const min = parseFloat(slider.min);
    const max = parseFloat(slider.max);
    const percent = ((value - min) / (max - min)) * 100;

    // Update fill
    const fillEl = container.querySelector(`[data-fsv5-slider-fill="${sliderType}"]`);
    if (fillEl) {
      fillEl.style.width = `${percent}%`;
    }

    // Update value display
    const valueEl = container.querySelector(`[data-fsv5-slider-value="${sliderType}"]`);
    if (valueEl) {
      const unit = valueEl.querySelector("small")?.textContent || "";
      valueEl.innerHTML = `${value}<small>${unit}</small>`;
    }

    // Update ARIA
    slider.setAttribute("aria-valuenow", value);
  }

  /**
   * Update slider state
   */
  function updateSliderState(sliderType, value) {
    const stateMap = {
      sail_size: "sailSize",
      board_volume: "boardVolume",
      board_width: "boardWidth",
    };

    const stateKey = stateMap[sliderType];
    if (stateKey) {
      selector.updateState(stateKey, value);
    }
  }

  /**
   * Setup find button
   */
  function setupFindButton(container) {
    const button = container.querySelector(SELECTORS.findButton);
    if (!button) return;

    button.addEventListener("click", () => {
      updateResultsRealtime();

      // Scroll to results on mobile
      if (window.innerWidth < 1024) {
        const resultsEl = container.querySelector(".fsv5-fullpage__results");
        if (resultsEl) {
          resultsEl.scrollIntoView({ behavior: "smooth" });
        }
      }
    });
  }

  // ============================================================================
  // RESULTS & FILTERING
  // ============================================================================

  /**
   * Debounced results update
   */
  function debouncedUpdateResults() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(updateResultsRealtime, DEBOUNCE_DELAY);
  }

  /**
   * Maximum results to display, controlled by the section setting when present.
   */
  function getMaxResults(container) {
    const grid = container?.querySelector(SELECTORS.resultsGrid);
    const configuredLimit = parseInt(grid?.dataset.fsv5ResultsLimit, 10);
    return Number.isFinite(configuredLimit) && configuredLimit > 0 ? configuredLimit : 6;
  }

  /**
   * Refresh live product data for rendered cards without stacking timers.
   */
  function scheduleVisibleProductRefresh() {
    if ("IntersectionObserver" in window) {
      observeLiveProductCards();
      return;
    }

    clearTimeout(visibleUpdateTimer);
    visibleUpdateTimer = setTimeout(() => {
      updateVisibleProductsInventory();
      updateVisibleProductsPrices();
    }, INVENTORY_UPDATE_DELAY);
  }

  /**
   * Update results in real-time based on current state
   */
  function updateResultsRealtime() {
    const state = selector.getState();
    const container = document.querySelector(SELECTORS.container);

    // Filter products
    const filtered = selector.filterProducts(products, state);

    // Sort by relevance
    const sorted = selector.sortByRelevance(filtered);

    // Limit to the section-configured number of cards
    const limited = sorted.slice(0, getMaxResults(container));

    // Render results
    renderResults(limited, container);

    // Update inventory and prices for newly rendered products.
    scheduleVisibleProductRefresh();

    // Update count
    updateResultsCount(sorted.length);

    // Update summary chips
    updateSummaryChips(state);

    // Update progress bar
    updateProgressBar();
  }

  /**
   * Render filtered products
   */
  function renderResults(filteredProducts, container) {
    const grid = container.querySelector(SELECTORS.resultsGrid);
    if (!grid) return;

    if (filteredProducts.length === 0) {
      grid.innerHTML = renderNoResults();
      return;
    }

    // Render product cards
    const state = selector.getState();
    const recommendedSize = state.recommendedSize;

    grid.innerHTML = filteredProducts
      .map((product, index) => {
        const isTopPick = index === 0 && recommendedSize && Math.abs(parseInt(product.fin_size) - recommendedSize) <= 1;

        return renderProductCard(product, isTopPick);
      })
      .join("");

    // Re-attach compare handlers
    setupCompareCheckboxes(container);
  }

  /**
   * Render performance bars for a product
   * Uses default values if performance data is not available
   */
  function renderPerformanceBars(product) {
    // Default performance values based on discipline
    const defaultPerf = getDefaultPerformance(product.discipline, product.fin_family);

    const speed = product.perf_speed || defaultPerf.speed;
    const control = product.perf_control || defaultPerf.control;
    const upwind = product.perf_upwind || defaultPerf.upwind;
    const release = product.perf_release || defaultPerf.release;

    // Only show performance bars if we have valid data
    if (!speed && !control && !upwind && !release) {
      return "";
    }

    return `
      <div class="fsv5-mini-perf">
        <div class="fsv5-mini-perf__item">
          <div class="fsv5-mini-perf__bar">
            <div class="fsv5-mini-perf__fill" style="width: ${speed * 10}%;"></div>
          </div>
          <div class="fsv5-mini-perf__label">Speed</div>
        </div>
        <div class="fsv5-mini-perf__item">
          <div class="fsv5-mini-perf__bar">
            <div class="fsv5-mini-perf__fill" style="width: ${control * 10}%;"></div>
          </div>
          <div class="fsv5-mini-perf__label">Control</div>
        </div>
        <div class="fsv5-mini-perf__item">
          <div class="fsv5-mini-perf__bar">
            <div class="fsv5-mini-perf__fill" style="width: ${upwind * 10}%;"></div>
          </div>
          <div class="fsv5-mini-perf__label">Upwind</div>
        </div>
        <div class="fsv5-mini-perf__item">
          <div class="fsv5-mini-perf__bar">
            <div class="fsv5-mini-perf__fill" style="width: ${release * 10}%;"></div>
          </div>
          <div class="fsv5-mini-perf__label">Release</div>
        </div>
      </div>
    `;
  }

  /**
   * 8 rating attributes from fin_model_family metaobject
   * No fallback - values come directly from metaobject
   */
  const RATING_ATTRIBUTES = [
    "speed",
    "control",
    "up_wind",
    "maneuverability",
    "power",
    "lift",
    "all_round",
    "cross_wind",
  ];

  /**
   * Get rating value from product (no fallback)
   */
  function getRating(product, attr) {
    const key = `rating_${attr}`;
    return product[key] || 0;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function escapeAttr(value) {
    return escapeHtml(value);
  }

  /**
   * Render a product card
   */
  function renderProductCard(product, isTopPick = false) {
    const productId = escapeAttr(product.id);
    const productHandle = escapeAttr(product.handle);
    const productTitle = escapeHtml(product.title);
    const productTitleAttr = escapeAttr(product.title);
    const productVendor = escapeHtml(product.vendor || "");
    const productVendorAttr = escapeAttr(product.vendor || "");
    const productUrl = escapeAttr(product.url || "#");
    const productImage = escapeAttr(product.featured_image || "");
    const productPrice = escapeHtml(product.price_formatted || "");

    // Stock status - show "loading" state initially, live inventory will update this
    // Check if we have live inventory data (inventory_quantity is set by live fetch)
    const hasLiveInventory = product._inventoryFetched === true;
    let stockStatus = "loading";
    let stockText = "Checking...";

    if (hasLiveInventory) {
      // We have live data - use it
      if (!product.available) {
        stockStatus = "out-of-stock";
        stockText = "Out of Stock";
      } else if (
        product.inventory_quantity !== undefined &&
        product.inventory_quantity <= 5 &&
        product.inventory_quantity > 0
      ) {
        stockStatus = "low-stock";
        stockText = `${product.inventory_quantity} left`;
      } else {
        stockStatus = "in-stock";
        stockText = "In Stock";
      }
    }

    // Construction dot class
    const constructionDotClass =
      product.construction_tier === "premium" || product.construction_tier === "performance"
        ? ""
        : "fsv5-product-card__construction-dot--warning";

    // Vendor logo - Check multiple sources
    let vendorLogo = product.vendor_logo || product.metafields?.custom?.vendor_logo || null;
    let vendorLogoIsUrl = false;

    // Fallback to known vendor logos if metafield is not set
    if (!vendorLogo && product.vendor) {
      if (product.vendor === "Select") {
        vendorLogo =
          "https://cdn.shopify.com/s/files/1/0689/9872/5855/files/Footer-Logo.SELECT-320x140px.png?v=1766812619";
        vendorLogoIsUrl = true;
      } else if (product.vendor === "Unifiber") {
        vendorLogo =
          "https://cdn.shopify.com/s/files/1/0689/9872/5855/files/200512_-_Unifiber_Logo_CLEAN_HQ_transparent.png?v=1708693522";
        vendorLogoIsUrl = true;
      }
    }

    // Render vendor logo or text
    let vendorHtml = "";
    if (vendorLogo) {
      if (vendorLogoIsUrl) {
        // Direct URL (for known vendors)
        vendorHtml = `<img
          src="${escapeAttr(vendorLogo)}"
          alt="${productVendorAttr}"
          class="fsv5-product-card__vendor"
          loading="lazy"
          width="60"
          height="24"
          style="max-width: 60px; height: auto; object-fit: contain;"
        >`;
      } else {
        // Metafield image (Shopify image object) - use image_url if available
        const imageUrl = vendorLogo.src || vendorLogo;
        vendorHtml = `<img
          src="${escapeAttr(imageUrl)}"
          alt="${productVendorAttr}"
          class="fsv5-product-card__vendor"
          loading="lazy"
          width="60"
          height="24"
          style="max-width: 60px; height: auto; object-fit: contain;"
        >`;
      }
    } else if (product.vendor) {
      vendorHtml = `<span class="fsv5-product-card__vendor fsv5-product-card__vendor--text">${productVendor}</span>`;
    }

    return `
      <article
        class="fsv5-product-card fsv5-product-card--compact"
        data-fsv5-product-id="${productId}"
        data-fsv5-product-handle="${productHandle}"
      >
        <div class="fsv5-product-card__header">
          ${vendorHtml}
          ${isTopPick ? '<span class="fsv5-product-card__badge">Top Pick</span>' : ""}
          <div class="fsv5-product-card__image">
            ${
              productImage
                ? `<img src="${productImage}" alt="${productTitleAttr}" loading="lazy">`
                : `<div class="fsv5-product-card__placeholder">${escapeHtml((product.vendor || "FIN").substring(0, 10).toUpperCase())}</div>`
            }
          </div>
        </div>

        <div class="fsv5-product-card__body">
          <h3 class="fsv5-product-card__name">
            <a href="${productUrl}">${productTitle}</a>
          </h3>
          <div class="fsv5-product-card__meta">
            ${
              product.construction
                ? `
              <div class="fsv5-product-card__construction">
                <span class="fsv5-product-card__construction-dot ${constructionDotClass}"></span>
                ${escapeHtml(product.construction)}
              </div>
            `
                : ""
            }
            <div class="fsv5-product-card__stock">
              <span class="fsv5-product-card__stock-dot fsv5-product-card__stock-dot--${stockStatus}"></span>
              <span class="fsv5-product-card__stock-text fsv5-product-card__stock-text--${stockStatus}">${stockText}</span>
            </div>
          </div>
          <div class="fsv5-product-card__footer">
            <div class="fsv5-product-card__price">
              ${productPrice}
            </div>
            ${
              hasLiveInventory
                ? product.available
                  ? `<button type="button" class="fsv5-product-card__btn" data-fsv5-add-to-cart>Add to Cart</button>`
                  : '<span class="fsv5-product-card__btn fsv5-product-card__btn--disabled">Out of Stock</span>'
                : `<button type="button" class="fsv5-product-card__btn" data-fsv5-add-to-cart>Add to Cart</button>`
            }
          </div>
        </div>
      </article>
    `;
  }

  /**
   * Render no results state
   */
  function renderNoResults() {
    return `
      <div class="fsv5-fullpage__no-results" data-fsv5-no-results>
        <div class="fsv5-fullpage__no-results-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        </div>
        <h3 class="fsv5-fullpage__no-results-title">No fins match your criteria</h3>
        <p class="fsv5-fullpage__no-results-text">Try adjusting your filters or equipment settings.</p>
      </div>
    `;
  }

  /**
   * Update results count display
   */
  function updateResultsCount(count) {
    const container = document.querySelector(SELECTORS.container);
    const countEl = container?.querySelector(SELECTORS.resultsCount);
    if (countEl) {
      const label = countEl.dataset.fsv5CountLabel || `fin${count !== 1 ? "s" : ""} match`;
      countEl.innerHTML = `<strong>${count}</strong> ${label}`;
    }
  }

  // ============================================================================
  // UI UPDATES
  // ============================================================================

  /**
   * Update summary chips in footer
   */
  function updateSummaryChips(state) {
    const container = document.querySelector(SELECTORS.container);
    const summaryEl = container?.querySelector(SELECTORS.summary);
    if (!summaryEl) return;

    const chips = [];

    if (state.skillLevel) {
      chips.push(createChip("Skill", capitalize(state.skillLevel)));
    }

    if (state.discipline) {
      chips.push(createChip("Discipline", capitalize(state.discipline)));
    }

    if (state.boxType) {
      chips.push(createChip("Box", FinSelectorV5.BOX_LABELS[state.boxType] || state.boxType));
    }

    if (state.antiweed) {
      chips.push(createChip("Type", "Antiweed"));
    }

    if (state.recommendedSize) {
      chips.push(createChip("Size", `${state.recommendedRange.min}-${state.recommendedRange.max}cm`, true));
    }

    summaryEl.innerHTML = chips.join("");
  }

  /**
   * Create a summary chip
   */
  function createChip(label, value, isRecommended = false) {
    const extraClass = isRecommended ? " fsv5-summary-tag--recommended" : "";
    return `
      <span class="fsv5-summary-tag${extraClass}">
        <span class="fsv5-summary-tag__label">${label}:</span>
        <span class="fsv5-summary-tag__value">${value}</span>
      </span>
    `;
  }

  /**
   * Update progress bar
   */
  function updateProgressBar() {
    const container = document.querySelector(SELECTORS.container);
    if (!container || !selector) return;

    const state = selector.getState();
    const steps = container.querySelectorAll(SELECTORS.progressStep);

    steps.forEach((step, index) => {
      step.classList.remove("fsv5-progress__step--completed", "fsv5-progress__step--active");

      let isComplete = false;
      switch (index) {
        case 0:
          isComplete = !!state.skillLevel;
          break;
        case 1:
          isComplete = !!state.discipline;
          break;
        case 2:
          isComplete = !!state.boxType;
          break;
        case 3:
          isComplete = state.sailSize !== 7.0 || state.boardWidth !== 70 || state.boardVolume !== 130;
          break;
      }

      if (isComplete) {
        step.classList.add("fsv5-progress__step--completed");
      }
    });
  }

  /**
   * Mark a section as complete
   */
  function markSectionComplete(sectionName) {
    const container = document.querySelector(SELECTORS.container);
    const section = container?.querySelector(`[data-fsv5-section="${sectionName}"]`);
    if (section) {
      section.dataset.fsv5SectionCompleted = "true";
    }
  }

  // ============================================================================
  // COMPARE FUNCTIONALITY
  // ============================================================================

  /**
   * Setup compare functionality
   */
  function setupCompare(container) {
    setupCompareCheckboxes(container);

    // Compare button
    const compareBtn = container.querySelector(SELECTORS.compareButton);
    if (compareBtn) {
      compareBtn.addEventListener("click", openCompareModal);
    }

    // Modal close
    const modal = document.querySelector(SELECTORS.compareModal);
    if (modal) {
      const closeBtn = modal.querySelector(SELECTORS.modalClose);
      if (closeBtn) {
        closeBtn.addEventListener("click", closeCompareModal);
      }

      // Click outside to close
      modal.addEventListener("click", (e) => {
        if (e.target === modal) {
          closeCompareModal();
        }
      });

      // Escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && modal.classList.contains("fsv5-modal-overlay--open")) {
          closeCompareModal();
        }
      });
    }
  }

  /**
   * Setup compare checkboxes on product cards
   */
  function setupCompareCheckboxes(container) {
    const checkboxes = container.querySelectorAll(SELECTORS.compareCheckbox);

    // Clone all checkboxes first to remove old listeners
    const clonedCheckboxes = [];
    checkboxes.forEach((checkbox) => {
      const clone = checkbox.cloneNode(true);
      checkbox.parentNode.replaceChild(clone, checkbox);
      clonedCheckboxes.push(clone);
    });

    // Add click listeners to the cloned elements
    clonedCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("click", handleCompareClick);
    });

    // Also query fresh in case cards were just rendered
    container.querySelectorAll(SELECTORS.compareCheckbox).forEach((checkbox) => {
      // Check if already has listener by looking for a marker
      if (!checkbox.dataset.fsv5CompareInit) {
        checkbox.dataset.fsv5CompareInit = "true";
        checkbox.addEventListener("click", handleCompareClick);
      }
    });
  }

  /**
   * Handle compare button click
   */
  function handleCompareClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const checkbox = e.currentTarget;
    const productId = checkbox.dataset.fsv5ProductId;

    if (!selector) {
      console.error("[FSV5 Fullpage] Selector not initialized");
      return;
    }

    const isSelected = selector.toggleCompare(productId);

    checkbox.classList.toggle("fsv5-product-card__compare--active", isSelected);
  }

  /**
   * Handle compare update from core
   */
  function handleCompareUpdate(action, productId, selectedIds) {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    // Update count display
    const countEl = container.querySelector(SELECTORS.compareCount);
    if (countEl) {
      countEl.textContent = `${selectedIds.length} fin${selectedIds.length !== 1 ? "s" : ""} selected`;
    }

    // Update compare button state
    const compareBtn = container.querySelector(SELECTORS.compareButton);
    if (compareBtn) {
      compareBtn.disabled = selectedIds.length < 2;
    }

    // Update all compare buttons in cards
    const allCompareButtons = container.querySelectorAll(SELECTORS.compareCheckbox);
    allCompareButtons.forEach((btn) => {
      const id = btn.dataset.fsv5ProductId;
      btn.classList.toggle("fsv5-product-card__compare--active", selectedIds.includes(id));
    });
  }

  /**
   * Open compare modal
   */
  function openCompareModal() {
    const modal = document.querySelector(SELECTORS.compareModal);
    if (!modal || !selector) return;

    const compareProducts = selector.getCompareProducts();
    if (compareProducts.length < 2) return;

    populateCompareModal(modal, compareProducts);

    modal.classList.add("fsv5-modal-overlay--open");
    modal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";

    const closeBtn = modal.querySelector(SELECTORS.modalClose);
    const compareBtn = document.querySelector(SELECTORS.compareButton);
    const a11y = window.UnifiberModalA11y;

    if (a11y) {
      compareA11yState = a11y.activate({
        roots: modal,
        trigger: compareBtn,
        focusTarget: closeBtn,
      });
    }
  }

  /**
   * Close compare modal
   */
  function closeCompareModal() {
    const modal = document.querySelector(SELECTORS.compareModal);
    if (!modal) return;

    modal.classList.remove("fsv5-modal-overlay--open");
    modal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";

    const a11y = window.UnifiberModalA11y;
    if (a11y && compareA11yState) {
      a11y.deactivate(compareA11yState);
      compareA11yState = null;
    }
  }

  /**
   * Populate compare modal with products
   */
  function populateCompareModal(modal, products) {
    const slots = modal.querySelectorAll("[data-fsv5-compare-slot]");

    slots.forEach((slot, index) => {
      const product = products[index];

      if (product) {
        slot.classList.remove("fsv5-compare-product--empty");

        const vendorEl = slot.querySelector("[data-fsv5-compare-vendor]");
        const imageEl = slot.querySelector("[data-fsv5-compare-image]");
        const nameEl = slot.querySelector("[data-fsv5-compare-name]");
        const priceEl = slot.querySelector("[data-fsv5-compare-price]");

        // Vendor logo - Check multiple sources
        if (vendorEl) {
          let vendorLogo = product.vendor_logo || product.metafields?.custom?.vendor_logo || null;
          let vendorLogoIsUrl = false;

          // Fallback to known vendor logos if metafield is not set
          if (!vendorLogo && product.vendor) {
            if (product.vendor === "Select") {
              vendorLogo =
                "https://cdn.shopify.com/s/files/1/0689/9872/5855/files/Footer-Logo.SELECT-320x140px.png?v=1766812619";
              vendorLogoIsUrl = true;
            } else if (product.vendor === "Unifiber") {
              vendorLogo =
                "https://cdn.shopify.com/s/files/1/0689/9872/5855/files/200512_-_Unifiber_Logo_CLEAN_HQ_transparent.png?v=1708693522";
              vendorLogoIsUrl = true;
            }
          }

          // Render vendor logo or text
          if (vendorLogo) {
            const imageUrl = vendorLogoIsUrl ? vendorLogo : vendorLogo.src || vendorLogo;
            vendorEl.innerHTML = `<img
              src="${imageUrl}"
              alt="${product.vendor || ""}"
              style="max-width: 100%; height: auto; max-height: 20px; object-fit: contain;"
              loading="lazy"
            >`;
          } else if (product.vendor) {
            vendorEl.textContent = product.vendor;
          } else {
            vendorEl.textContent = "";
          }
        }
        if (imageEl) {
          imageEl.innerHTML = product.featured_image
            ? `<img src="${product.featured_image}" alt="${product.title}" loading="lazy">`
            : `<span class="fsv5-compare-product__placeholder">${product.vendor || "FIN"}</span>`;
        }
        if (nameEl) nameEl.textContent = product.title;
        if (priceEl) priceEl.textContent = product.price_formatted;

        // Update spec values - 8 metaobject ratings (no fallback)
        RATING_ATTRIBUTES.forEach((attr) => {
          const specEl = modal.querySelector(`[data-fsv5-spec-value="${index}"][data-spec-type="${attr}"]`);
          if (specEl) {
            const val = getRating(product, attr);
            specEl.textContent = val ? `${val}/10` : "-";
          }
        });

        // Price row
        const priceSpecEl = modal.querySelector(`[data-fsv5-spec-price="${index}"]`);
        if (priceSpecEl) {
          priceSpecEl.textContent = product.price_formatted;
        }
      } else {
        slot.classList.add("fsv5-compare-product--empty");
        const nameEl = slot.querySelector("[data-fsv5-compare-name]");
        const priceEl = slot.querySelector("[data-fsv5-compare-price]");
        const imageEl = slot.querySelector("[data-fsv5-compare-image]");

        if (nameEl) nameEl.textContent = "Select a fin";
        if (priceEl) priceEl.textContent = "-";
        if (imageEl) imageEl.innerHTML = '<span class="fsv5-compare-product__placeholder">+ Add Fin</span>';
      }
    });

    // Update radar chart (if implemented)
    updateRadarChart(modal, products);
  }

  /**
   * Update radar chart with product data
   */
  function updateRadarChart(modal, products) {
    const legend = modal.querySelector("[data-fsv5-radar-legend]");
    if (!legend) return;

    // Update legend
    legend.innerHTML = products
      .map(
        (product, index) => `
      <div class="fsv5-radar__legend-item">
        <span class="fsv5-radar__legend-color fsv5-radar__legend-color--${index + 1}"></span>
        <span>${product.title}</span>
      </div>
    `,
      )
      .join("");

    // Update radar polygons - 8 axes octagon from metaobject ratings (no fallback)
    const center = 160;
    const maxRadius = 120;
    const angles = [0, 45, 90, 135, 180, 225, 270, 315]; // 8 axes

    products.forEach((product, index) => {
      const polygon = modal.querySelector(`[data-fsv5-radar-data="${index}"]`);
      if (!polygon) return;

      // Get all 8 rating values from metaobject (no fallback)
      const values = RATING_ATTRIBUTES.map((attr) => {
        const val = getRating(product, attr);
        return Math.max(0, Math.min(10, val));
      });

      // Calculate polygon points for octagon
      const points = values
        .map((val, i) => {
          const angleRad = (angles[i] - 90) * (Math.PI / 180);
          const radius = (val / 10) * maxRadius;
          const x = center + radius * Math.cos(angleRad);
          const y = center + radius * Math.sin(angleRad);
          return `${Math.round(x)},${Math.round(y)}`;
        })
        .join(" ");

      polygon.setAttribute("points", points);
    });
  }

  // ============================================================================
  // INFO MODAL
  // ============================================================================

  /**
   * Info modal state (focus trap + restore)
   */
  let infoModalState = null;

  /**
   * Setup info modal functionality
   */
  function setupInfoModal(container) {
    // Use event delegation for info buttons (cards are dynamically rendered)
    container.addEventListener("click", (e) => {
      const infoBtn = e.target.closest(SELECTORS.infoButton);
      if (infoBtn) {
        e.preventDefault();
        e.stopPropagation();
        openInfoModal(infoBtn);
      }
    });

    // Close button
    const modal = document.querySelector(SELECTORS.infoModal);
    if (modal) {
      const closeBtn = modal.querySelector(SELECTORS.infoClose);
      if (closeBtn) {
        closeBtn.addEventListener("click", closeInfoModal);
      }

      // Click outside to close
      modal.addEventListener("click", (e) => {
        if (e.target === modal) {
          closeInfoModal();
        }
      });

      // Escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && modal.getAttribute("aria-hidden") === "false") {
          closeInfoModal();
        }
      });
    }
  }

  /**
   * Open info modal with product data
   */
  function openInfoModal(btn) {
    const modal = document.querySelector(SELECTORS.infoModal);
    if (!modal) return;

    const productId = btn.dataset.fsv5ProductId;
    const product = products.find((p) => String(p.id) === productId);

    // Get data from button attributes or product object
    const headline = btn.dataset.headline || product?.headline || "-";
    const usp = btn.dataset.usp || product?.usp || "-";
    const target = btn.dataset.target || product?.target_rider || "-";
    const sizes = btn.dataset.sizes || product?.size_range || "-";
    const title = product?.title || "Product Details";

    // Populate modal
    const titleEl = modal.querySelector("[data-fsv5-info-title]");
    const headlineEl = modal.querySelector("[data-fsv5-info-headline]");
    const uspEl = modal.querySelector("[data-fsv5-info-usp]");
    const targetEl = modal.querySelector("[data-fsv5-info-target]");
    const sizesEl = modal.querySelector("[data-fsv5-info-sizes]");

    if (titleEl) titleEl.textContent = title;
    if (headlineEl) headlineEl.textContent = headline;
    if (uspEl) uspEl.textContent = usp;
    if (targetEl) targetEl.textContent = target;
    if (sizesEl) sizesEl.textContent = sizes;

    // Show modal
    modal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";

    if (window.UnifiberModalA11y) {
      infoModalState = window.UnifiberModalA11y.activate({
        roots: modal,
        trigger: btn,
        focusTarget: modal.querySelector(SELECTORS.infoClose),
      });
    }
  }

  /**
   * Close info modal
   */
  function closeInfoModal() {
    const modal = document.querySelector(SELECTORS.infoModal);
    if (!modal) return;

    modal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";

    if (infoModalState && window.UnifiberModalA11y) {
      window.UnifiberModalA11y.deactivate(infoModalState);
      infoModalState = null;
    }
  }

  // ============================================================================
  // ACCORDION
  // ============================================================================

  /**
   * Setup accordion interactions
   */
  function setupAccordion(container) {
    const triggers = container.querySelectorAll(SELECTORS.accordionTrigger);

    triggers.forEach((trigger) => {
      trigger.addEventListener("click", () => {
        const item = trigger.closest(SELECTORS.accordionItem);
        if (!item) return;

        const isOpen = item.classList.contains("fsv5-accordion__item--open");

        // Close all items in this accordion
        const accordion = item.parentElement;
        accordion.querySelectorAll(SELECTORS.accordionItem).forEach((i) => {
          i.classList.remove("fsv5-accordion__item--open");
          i.querySelector(SELECTORS.accordionTrigger)?.setAttribute("aria-expanded", "false");
        });

        // Toggle current item
        if (!isOpen) {
          item.classList.add("fsv5-accordion__item--open");
          trigger.setAttribute("aria-expanded", "true");
        }
      });
    });
  }

  // ============================================================================
  // ADD TO CART
  // ============================================================================

  /**
   * Setup add to cart button handlers
   */
  function setupAddToCart(container) {
    // Use event delegation for add to cart buttons (cards are dynamically rendered)
    container.addEventListener("click", async (e) => {
      const btn = e.target.closest("[data-fsv5-add-to-cart]");
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      // Get product handle from card
      const card = btn.closest("[data-fsv5-product-handle]");
      if (!card) {
        console.error("[FSV5 Fullpage] Product card not found for add to cart");
        return;
      }

      const productHandle = card.dataset.fsv5ProductHandle;
      if (!productHandle) {
        console.error("[FSV5 Fullpage] Product handle not found");
        return;
      }

      await handleAddToCart(btn, productHandle);
    });
  }

  /**
   * Handle add to cart action
   * @param {HTMLElement} button - The add to cart button
   * @param {string} productHandle - Product handle
   */
  async function handleAddToCart(button, productHandle) {
    const originalText = button.textContent;
    const originalDisabled = button.disabled;

    // Set loading state
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    button.textContent = "Adding...";

    try {
      // Fetch product data to get variant ID
      const productResponse = await fetch(`/products/${productHandle}.js`);
      if (!productResponse.ok) {
        throw new Error(`Failed to fetch product: ${productResponse.status}`);
      }

      const product = await productResponse.json();

      // Get first available variant, or first variant if none available
      const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
      if (!variant) {
        throw new Error("No variants found for product");
      }

      // Prepare sections for cart drawer
      const sectionsToBundle = ["variant-added"];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }),
      );

      // Get root path (fallback to '/' if Shopify.routes not available)
      const rootPath = window.Shopify?.routes?.root || "/";

      // Add to cart
      const formData = new FormData();
      formData.set("id", variant.id);
      formData.set("quantity", "1");
      formData.set("sections", sectionsToBundle.join(","));
      formData.set("sections_url", `${rootPath}variants/${variant.id}`);

      const addResponse = await fetch(`${rootPath}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.description || "Failed to add to cart");
      }

      const addResponseJson = await addResponse.json();

      // Fetch updated cart
      const cartResponse = await fetch(`${rootPath}cart.js`);
      if (!cartResponse.ok) {
        throw new Error("Failed to fetch cart");
      }

      const cartContent = await cartResponse.json();

      // Merge sections from add response into cart object
      cartContent.sections = addResponseJson.sections || {};

      // Dispatch variant:add event (triggers cart drawer)
      document.documentElement.dispatchEvent(
        new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: addResponseJson.hasOwnProperty("items") ? addResponseJson.items : [addResponseJson],
            cart: cartContent,
          },
        }),
      );

      // Dispatch cart:change event (updates cart count)
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent,
          },
        }),
      );

      // Show success state
      button.textContent = "Added!";
      button.classList.add("fsv5-product-card__btn--success");

      // Reset after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove("fsv5-product-card__btn--success");
        button.disabled = originalDisabled;
        button.removeAttribute("aria-busy");
      }, 2000);
    } catch (error) {
      console.error("[FSV5 Fullpage] Add to cart error:", error);

      // Show error state
      button.textContent = "Error - Try Again";
      button.classList.add("fsv5-product-card__btn--error");

      // Reset after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove("fsv5-product-card__btn--error");
        button.disabled = originalDisabled;
        button.removeAttribute("aria-busy");
      }, 3000);
    }
  }

  // ============================================================================
  // STATE HANDLERS
  // ============================================================================

  /**
   * Handle state changes from core
   */
  function handleStateChange(key, newValue, oldValue) {
    // Calculate recommended size when relevant values change
    if (["discipline", "sailSize", "boardWidth", "boardVolume", "antiweed"].includes(key)) {
      selector.calculateRecommendedSize();
    }
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Capitalize first letter
   */
  function capitalize(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  // ============================================================================
  // INIT ON READY
  // ============================================================================

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFinSelectorFullpage);
  } else {
    initFinSelectorFullpage();
  }
})();
